import { z } from "zod";
// Reuse the canonical Json type + schema from the shared barrel's first module
// so `export *` from index.ts has exactly one `Json` / `jsonSchema` symbol.
import { jsonSchema } from "./service-requests";

/**
 * Expenses shared contract (Strangler-Fig migration, manifest-6) — FINANCIAL slice.
 *
 * Single source of truth for the `expenses` wire shape shared by the web SPA
 * (`src/repositories/expenses.ts`) and the NestJS slice (`apps/api/src/expenses/*`).
 * Shape is **snake_case** so the frontend<->Nest transport is a field-for-field
 * swap (no renames), matching the Supabase generated `expenses` Row and the Nest
 * repository `.select()` lists.
 *
 * The web repo `.parse()`s Nest responses against {@link expenseSchema} at the
 * flip boundary, so any backend/frontend drift surfaces at runtime (and the repo
 * falls back to the Supabase path).
 *
 * ⚠️ TOLERANCE RULE (the #1699 silent-no-op lesson): the staff list/detail
 * queries return the expense row JOINED with nested `property` / `unit` /
 * `contract` / `generated_invoice` relations whose column subset differs between
 * the list select and the detail select, and may be `null` (FK nullable). So the
 * relation fields are `.optional()` + `.nullable()` with a **passthrough** object
 * shape — a too-strict relation schema would make every Nest call silently fall
 * back to Supabase. The whole object is `.passthrough()` so new migration columns
 * never reject a valid payload.
 *
 * ⚠️ MONEY-FIELD TOLERANCE (financial parity): `amount` / `total_amount` /
 * `vat_amount` / `vat_rate` are Postgres `numeric` columns. supabase-js usually
 * deserializes them to JS `number`, but PostgREST can emit `numeric` as a string
 * for very large/precise values. `money` accepts BOTH so a string-encoded amount
 * never triggers a silent fallback — the halala-exact parity check in the live
 * smoke compares the VALUES, not the wire type. Consumers coerce with Number().
 *
 * BE adoption note: `apps/api` does not yet import this package — it keeps its
 * class-validator DTO (already shape-aligned). Wiring the Nest side to consume
 * `@dallali/shared` needs the dual ESM/CJS build step tracked in
 * `docs/migration/domain-debugging-matrix.md`.
 */

/** Expense kinds (advisory — `expense_type` stays an open string to never reject server data). */
export const EXPENSE_TYPES = [
  "maintenance",
  "utilities",
  "management",
  "insurance",
  "tax",
  "other",
] as const;

/** Who bears the expense (advisory — stays an open string). */
export const PAYMENT_RESPONSIBILITIES = ["owner", "tenant", "company"] as const;

/**
 * A money column that tolerates number OR numeric-string (PostgREST `numeric`
 * serialization) and null. The parity gate compares coerced numeric values.
 */
const money = z.union([z.number(), z.string()]).nullable();

/**
 * Loose, passthrough relation shape. The staff list/detail selects embed
 * different column subsets of property/unit/contract/generated_invoice; keep
 * this permissive so the parser never rejects a valid joined payload (#1699
 * lesson). `null` because the FKs are nullable.
 */
const relation = z.record(jsonSchema).nullable().optional();

/**
 * A persisted expense row as the staff Finance dashboard consumes it. The
 * own-columns field set + nullability mirror the Supabase generated `expenses`
 * Row, so it can be the single source of truth without changing any consumer.
 * The four nested relations are appended by the list/detail joins.
 */
export const expenseSchema = z
  .object({
    id: z.string(),
    company_id: z.string(),
    property_id: z.string().nullable(),
    unit_id: z.string().nullable(),
    contract_id: z.string().nullable(),
    expense_date: z.string(),
    expense_type: z.string(),
    payment_responsibility: z.string(),
    amount: money,
    vat_rate: money,
    vat_amount: money,
    total_amount: money,
    vendor_name: z.string().nullable(),
    description: z.string(),
    invoice_url: z.string().nullable(),
    images: jsonSchema.nullable(),
    is_invoiced: z.boolean().nullable(),
    generated_invoice_id: z.string().nullable(),
    generated_invoice_url: z.string().nullable(),
    deleted_at: z.string().nullable(),
    created_at: z.string(),
    updated_at: z.string(),
    // Joined relations (list + detail) — loose + optional + nullable.
    property: relation,
    unit: relation,
    contract: relation,
    generated_invoice: relation,
  })
  // Tolerate extra columns the server may add (new migration columns) without
  // forcing a same-PR schema bump — keeps the flip from silently falling back.
  .passthrough();

export type Expense = z.infer<typeof expenseSchema>;
